import { TIME_ZONE_UTC } from '@/constants';
import type { MagicEventFragment } from '@/features/events/fragments/MagicEventFragment.generated';
import type { DecoratedMagicEvent } from '@/features/magic/types';
import {
  createEntryDate,
  getLocalTimeZone,
  toExclusiveEndDate,
} from '@/utils/dates';

export const createDecoratedMagicEvent = (
  rawEvent: MagicEventFragment,
  timeZone: string = getLocalTimeZone()
): DecoratedMagicEvent => {
  const { isAllDay } = rawEvent;

  const startDate = createEntryDate(
    isAllDay ? rawEvent.start.date! : rawEvent.start.dateTime!,
    rawEvent.start.timeZone!,
    timeZone,
    isAllDay
  );
  const endDate = createEntryDate(
    isAllDay ? rawEvent.end.date! : rawEvent.end.dateTime!,
    rawEvent.end.timeZone!,
    timeZone,
    isAllDay
  );
  const lastEndDate = rawEvent.lastEndDate
    ? createEntryDate(
        rawEvent.lastEndDate,
        rawEvent.end.timeZone!,
        timeZone,
        isAllDay
      )
    : null;

  // Decoding is the one place every reader and every date-preserving write
  // passes through, so hold the exclusive-end invariant here rather than leaving
  // each consumer to defend against a violation.
  const exclusiveEndDate = isAllDay
    ? toExclusiveEndDate(startDate, endDate)
    : endDate;

  // `lastEndDate` is the exclusive end of the final occurrence, so it measures
  // the same span as `endDate` and has to move with it. Shift it by the same
  // number of days rather than clamping it against `startDate`, which is a no-op
  // for a recurring series whose last end already sits well past the start.
  const shiftedDays = Math.round(exclusiveEndDate.diff(endDate, 'days').days);

  return {
    ...rawEvent,
    createdAt: new Date(rawEvent.createdAt),
    updatedAt: rawEvent.updatedAt ? new Date(rawEvent.updatedAt) : null,
    timeZone: isAllDay ? TIME_ZONE_UTC : (rawEvent.start.timeZone ?? timeZone),
    startDate,
    endDate: exclusiveEndDate,
    lastEndDate:
      lastEndDate && shiftedDays > 0
        ? lastEndDate.plus({ days: shiftedDays })
        : lastEndDate,
    lastEndDateTime: rawEvent.lastEndDateTime
      ? createEntryDate(
          rawEvent.lastEndDateTime,
          rawEvent.end.timeZone!,
          timeZone,
          isAllDay
        )
      : null,
  };
};
